import React, { useState } from 'react';
import { useWishlist } from '../../../context/WishlistContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductInfo = ({ product = {}, onAddToCart, onAddToWishlist }) => {
  const wishlist = useWishlist();
  const [selectedVariant, setSelectedVariant] = useState(0);
  const [quantity, setQuantity] = useState(1);
  // isWishlisted is derived from the wishlist context (no local state).

  // No hardcoded fallback — the parent guards rendering until product loads.
  const productData = product || {};
  const currentVariant = productData?.variants?.[selectedVariant];
  const discount = productData?.originalPrice ? 
    Math.round(((productData?.originalPrice - productData?.price) / productData?.originalPrice) * 100) : 0;

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= 10) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = () => {
    if (onAddToCart) {
      onAddToCart({
        ...productData,
        variant: currentVariant,
        quantity
      });
    }
  };

  const isWishlisted = wishlist.has(productData?.id);
  const handleWishlistToggle = () => {
    wishlist.toggle(productData);
    if (onAddToWishlist) onAddToWishlist(productData);
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars?.push(
        <Icon key={i} name="Star" size={16} className="text-warning fill-current" />
      );
    }

    if (hasHalfStar) {
      stars?.push(
        <Icon key="half" name="StarHalf" size={16} className="text-warning fill-current" />
      );
    }

    const emptyStars = 5 - Math.ceil(rating);
    for (let i = 0; i < emptyStars; i++) {
      stars?.push(
        <Icon key={`empty-${i}`} name="Star" size={16} className="text-muted-foreground" />
      );
    }

    return stars;
  };

  return (
    <div className="space-y-6">
      {/* Brand and Title */}
      <div>
        <p className="text-sm font-medium text-primary mb-2">{productData?.brand}</p>
        <h1 className="text-3xl font-heading font-semibold text-foreground leading-tight">
          {productData?.name}
        </h1>
      </div>
      {/* Rating and Reviews */}
      <div className="flex items-center space-x-4">
        <div className="flex items-center space-x-1">
          {renderStars(productData?.rating)}
        </div>
        <span className="text-sm font-medium text-foreground">{productData?.rating}</span>
        <span className="text-sm text-muted-foreground">
          ({productData?.reviewCount?.toLocaleString()} reviews)
        </span>
      </div>
      {/* Pricing */}
      <div className="flex items-center space-x-3">
        <span className="text-3xl font-bold text-foreground">
          ${(currentVariant?.price ?? productData?.price ?? 0).toFixed(2)}
        </span>
        {productData?.originalPrice && (
          <>
            <span className="text-xl text-muted-foreground line-through">
              ${productData?.originalPrice?.toFixed(2)}
            </span>
            <span className="bg-error text-error-foreground text-sm font-medium px-2 py-1 rounded-lg">
              {discount}% OFF
            </span>
          </>
        )}
      </div>
      {/* Stock Status */}
      <div className="flex items-center space-x-2">
        <Icon 
          name={productData?.inStock ? "CheckCircle" : "XCircle"} 
          size={16} 
          className={productData?.inStock ? "text-success" : "text-error"} 
        />
        <span className={`text-sm font-medium ${
          productData?.inStock ? "text-success" : "text-error"
        }`}>
          {productData?.inStock 
            ? `In Stock (${productData?.stockCount} available)` 
            : 'Out of Stock'
          }
        </span>
      </div>
      {/* Variant Selection */}
      {productData?.variants && productData?.variants?.length > 1 && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Size
          </label>
          <div className="flex space-x-3">
            {productData?.variants?.map((variant, index) => (
              <button
                key={variant?.id}
                onClick={() => setSelectedVariant(index)}
                disabled={!variant?.inStock}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
                  index === selectedVariant
                    ? 'border-primary bg-primary text-primary-foreground'
                    : variant?.inStock
                    ? 'border-border bg-background text-foreground hover:border-primary'
                    : 'border-border bg-muted text-muted-foreground cursor-not-allowed'
                }`}
              >
                {variant?.name}
                {!variant?.inStock && (
                  <span className="block text-xs">Out of Stock</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Quantity and Actions */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-3">
            Quantity
          </label>
          <div className="flex items-center space-x-3">
            <div className="flex items-center border border-border rounded-lg">
              <button
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 1}
                className="p-2 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="Minus" size={16} />
              </button>
              <span className="px-4 py-2 text-sm font-medium text-foreground min-w-[3rem] text-center">
                {quantity}
              </span>
              <button
                onClick={() => handleQuantityChange(1)}
                disabled={quantity >= 10}
                className="p-2 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Icon name="Plus" size={16} />
              </button>
            </div>
            <span className="text-sm text-muted-foreground">
              Maximum 10 per order
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <Button
            onClick={handleAddToCart}
            disabled={!productData?.inStock}
            iconName="ShoppingCart"
            iconPosition="left"
            className="flex-1"
          >
            Add to Cart
          </Button>
          <Button
            variant="outline"
            onClick={handleWishlistToggle}
            iconName={isWishlisted ? "Heart" : "Heart"}
            className={isWishlisted ? "text-error border-error" : ""}
          >
            {isWishlisted ? 'Saved' : 'Save'}
          </Button>
        </div>
      </div>
      {/* Product Features */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4">
          Product Features
        </h3>
        <div className="grid grid-cols-2 gap-3">
          {productData?.features?.map((feature, index) => (
            <div key={index} className="flex items-center space-x-2">
              <Icon name="Check" size={16} className="text-success flex-shrink-0" />
              <span className="text-sm text-foreground">{feature}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Quick Info */}
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Truck" size={20} className="text-accent flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-accent-foreground">
              Free shipping on orders over $50
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Estimated delivery: 3-5 business days
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductInfo;